import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../redux/hooks/useAuth';
import { useTheme } from '../redux/hooks/useTheme';
import { useThemeColors } from '../theme/hooks';
import AuthLoadingScreen from './AuthLoadingScreen';

import { 
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings,
  Logout,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  LightMode,
  DarkMode
} from '@mui/icons-material';
import ClientSelector from './ClientSelector';

const Header = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();
  const colors = useThemeColors();
  const {
    user,
    userRole,
    isSuperAdmin,
    hasPermission,
    logoutUser,
    userName
  } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    setIsLoggingOut(true);
    
    try {
      console.log('🚪 Header: Starting logout process...');
      await logoutUser();
      console.log('✅ Header: Logout successful, redirecting to login...');
      
      // Show loading screen for a moment before redirecting
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 1500);
    } catch (error) {
      console.error('❌ Header: Logout failed:', error);
      // Even if logout fails, redirect to login
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 1500);
    }
  };

  const handleProfile = () => {
    handleClose();
    navigate('/settings');
  };

  const getRoleColor = () => {
    if (isSuperAdmin) return 'error';
    if (userRole === 'client_hr_manager') return 'warning';
    if (userRole === 'client_interviewer') return 'info';
    return 'default';
  };

  const getRoleLabel = () => {
    if (isSuperAdmin) return 'Admin';
    if (userRole === 'client_hr_manager') return 'HR Manager';
    if (userRole === 'client_interviewer') return 'Interviewer';
    if (userRole === 'client_viewer') return 'Viewer';
    return userRole || 'User';
  };

  // Show loading screen during logout
  if (isLoggingOut) {
    return <AuthLoadingScreen type="logout" />;
  }

  const getRoleIcon = () => {
    if (isSuperAdmin) return <AdminIcon />;
    return <PersonIcon />;
  };

  const getUserDisplayName = () => {
    return userName || 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserEmail = () => {
    return user?.email || '';
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: colors.gradient.background,
        // borderBottom: '1px solid',
        // borderColor: 'divider',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
       

        {/* HyreX Logo and Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img 
              src="/icons/hyrex-favicon-32x32.png" 
              alt="HyreX Logo" 
              style={{ 
                width: 32, 
                height: 32,
                borderRadius: '4px'
              }}
            />
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                background: colors.gradient.primary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
                fontSize: '1.25rem'
              }}
            >
              HyreX
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Client Selector for Super Admin */}
          {isSuperAdmin && <ClientSelector />}
          
          {/* Theme Toggle Button */}
          <IconButton
            onClick={toggleTheme}
            sx={{ 
              background: colors.gradient.primary,
              color: 'white',
              '&:hover': { 
                background: colors.gradient.secondary,
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>

          {/* Logout Icon Button */}
          <IconButton
            size="medium"
            aria-label="logout"
            onClick={handleLogout}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.main',
                color: 'white',
              }
            }}
          >
            <Logout fontSize="small" />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 